import React, { useState, useEffect } from "react";
import "../styles/ChairRegistrationModal.css";
import {
  ChairRegistrationModalProps,
  FormData,
  FormErrors,
  Student,
} from "../types";
import { useEnvironment } from "../contexts/EnvironmentContext";
import { getUnAllottedStudentDetails } from "../api/StudentDetails";
import { useWebSocketRFID } from "../hooks/useWebSocketRFID";

const ChairRegistrationModal: React.FC<ChairRegistrationModalProps> = ({
  isOpen,
  onClose,
  onRegister,
  chairNumber,
  currentOccupant,
}) => {
  const { config } = useEnvironment();
  const [formData, setFormData] = useState<FormData>({
    studentName: "",
    rfid: "",
    mobileNumber: "",
    email: "",
    studentType: "registered", // 'registered' or 'test'
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // WebSocket connection for RFID reading (only when modal is open and in registered mode)
  const shouldEnableWS = isOpen && formData.studentType === "registered";
  const { isConnected: wsConnected, error: wsError } = useWebSocketRFID({
    url: "wss://nodewebsocket.omsaireadingroom.in",
    enabled: shouldEnableWS,
    onMessage: (rfid) => {
      console.log("RFID received from WebSocket:", rfid);
      // Auto-populate RFID field when data is received (only for registered students)
      setFormData((prev) => {
        if (prev.studentType === "registered") {
          return {
            ...prev,
            rfid: rfid,
          };
        }
        return prev;
      });
      // Clear RFID error if any
      setErrors((prev) => ({
        ...prev,
        rfid: "",
      }));
    },
  });

  // Fetch students when modal opens
  useEffect(() => {
    const fetchStudents = async () => {
      if (isOpen) {
        setLoading(true);
        try {
          const response = await getUnAllottedStudentDetails();
          setStudents(response.data || []);
        } catch (error) {
          console.error("Error fetching students:", error);
          setStudents([]);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchStudents();
  }, [isOpen]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      const occupantType = currentOccupant?.studentType || "registered";
      setFormData({
        studentName: currentOccupant?.name || "",
        rfid: currentOccupant?.rfid || "",
        mobileNumber: currentOccupant?.mobileNumber || "",
        email: currentOccupant?.email || "",
        studentType: occupantType,
      });
      setErrors({});
    }
  }, [isOpen, currentOccupant]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleStudentSelect = (
    e: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const selectedStudentId = e.target.value;

    if (selectedStudentId) {
      const selectedStudent = students.find(
        (student) =>
          `${student.firstName} ${student.lastName}` === selectedStudentId
      );

      if (selectedStudent) {
        setFormData((prev) => ({
          ...prev,
          studentName: selectedStudentId,
          rfid: selectedStudent.rfIdentificationCode,
          email: selectedStudent.emailId,
          mobileNumber: selectedStudent.mobileNumber,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        studentName: "",
        rfid: "",
        email: "",
        mobileNumber: "",
      }));
    }

    // Clear errors
    setErrors((prev) => ({
      ...prev,
      studentName: "",
      rfid: "",
    }));
  };

  const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newStudentType = e.target.checked ? "test" : "registered";

    setFormData((prev) => ({
      ...prev,
      studentName: "",
      rfid: "",
      mobileNumber: "",
      email: "",
      studentType: newStudentType,
    }));

    // Clear errors when switching modes
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.studentName.trim()) {
      newErrors.studentName = "Student name is required";
    } else if (formData.studentName.trim().length < 2) {
      newErrors.studentName = "Student name must be at least 2 characters";
    }

    if (formData.studentType === "registered") {
      // For registered students, RFID is required
      if (!formData.rfid.trim()) {
        newErrors.rfid = "RFID is required";
      } else if (formData.rfid.trim().length < 3) {
        newErrors.rfid = "RFID must be at least 3 characters";
      }
    } else {
      // For test students, mobile number is required
      if (!formData.mobileNumber.trim()) {
        newErrors.mobileNumber = "Mobile number is required";
      } else if (formData.mobileNumber.trim().length < 10) {
        newErrors.mobileNumber = "Mobile number must be at least 10 digits";
      }

      // Email is optional for test students, but if provided, should be valid
      if (formData.email.trim() && !isValidEmail(formData.email.trim())) {
        newErrors.email = "Please enter a valid email address";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    if (validateForm() && chairNumber) {
      onRegister(chairNumber, {
        name: formData.studentName.trim(),
        rfid: formData.studentType === "registered" ? formData.rfid.trim() : "",
        mobileNumber:
          formData.studentType === "test" ? formData.mobileNumber.trim() : "",
        email:
          formData.studentType === "test"
            ? formData.email.trim()
            : `${formData.studentName
                .toLowerCase()
                .replace(/\s+/g, ".")}@university.edu`,
        studentType: formData.studentType,
        role: formData.studentType === "registered" ? "Student" : "Test User",
        department:
          formData.studentType === "registered" ? "Academic" : "Testing",
        status: "occupied",
      });
      onClose();
    }
  };

  const handleCancel = (): void => {
    setFormData({
      studentName: "",
      rfid: "",
      mobileNumber: "",
      email: "",
      studentType: "registered",
    });
    setErrors({});
    onClose();
  };

  if (!isOpen || !config.allowRegistration) return null;

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Chair Registration - A-{chairNumber}</h2>
          <button className="close-btn" onClick={handleCancel}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="studentName" className="form-label">
              Student Name *{" "}
              {formData.studentType === "registered" &&
                students.length > 0 &&
                `(${students.length} students available)`}
            </label>
            {formData.studentType === "registered" ? (
              <select
                id="studentName"
                name="studentName"
                value={formData.studentName}
                onChange={handleStudentSelect}
                className={`form-input ${errors.studentName ? "error" : ""}`}
                disabled={loading}
                autoFocus
              >
                <option value="">
                  {loading
                    ? "Loading students..."
                    : students.length === 0
                    ? "No students available"
                    : "Select a student"}
                </option>
                {students.map((student) => (
                  <option
                    key={`${student.firstName}-${student.lastName}`}
                    value={`${student.firstName} ${student.lastName}`}
                  >
                    {student.firstName} {student.lastName}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                id="studentName"
                name="studentName"
                value={formData.studentName}
                onChange={handleInputChange}
                className={`form-input ${errors.studentName ? "error" : ""}`}
                placeholder="Enter test student name"
                autoFocus
              />
            )}
            {errors.studentName && (
              <span className="error-message">{errors.studentName}</span>
            )}
          </div>

          {formData.studentType === "registered" ? (
            <div className="form-group">
              <label htmlFor="rfid" className="form-label">
                RFID *{" "}
                <span className="field-note">
                  (Auto-filled from student selection or RFID reader)
                </span>
                {shouldEnableWS && wsConnected && (
                  <span className="ws-status" style={{ marginLeft: "8px", fontSize: "0.85em", color: "#10b981", fontWeight: "500" }}>
                    ● Reader Connected
                  </span>
                )}
              </label>
              <input
                type="text"
                id="rfid"
                name="rfid"
                value={formData.rfid}
                onChange={handleInputChange}
                className={`form-input ${errors.rfid ? "error" : ""}`}
                placeholder={wsConnected ? "RFID will be auto-filled from reader" : "RFID will be auto-filled"}
                readOnly
                style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
              />
              {errors.rfid && (
                <span className="error-message">{errors.rfid}</span>
              )}
              <span className="form-hint" style={{ fontSize: "0.85em", marginTop: "4px" }}>
                {wsConnected
                  ? "RFID reader connected - scan a card to auto-fill"
                  : "Select a student or wait for RFID reader connection"}
              </span>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="mobileNumber" className="form-label">
                  Mobile Number *{" "}
                  <span className="field-note">
                    (Required for test students)
                  </span>
                </label>
                <input
                  type="tel"
                  id="mobileNumber"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleInputChange}
                  className={`form-input ${errors.mobileNumber ? "error" : ""}`}
                  placeholder="Enter mobile number"
                />
                {errors.mobileNumber && (
                  <span className="error-message">{errors.mobileNumber}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`form-input ${errors.email ? "error" : ""}`}
                  placeholder="Enter email address (optional)"
                />
                {errors.email && (
                  <span className="error-message">{errors.email}</span>
                )}
              </div>
            </>
          )}

          {config.showTestStudentToggle && (
            <div className="form-group">
              <label className="form-label">Student Type</label>
              <div className="toggle-container">
                <span
                  className={`toggle-label ${
                    formData.studentType === "registered" ? "active" : ""
                  }`}
                >
                  Registered Student
                </span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={formData.studentType === "test"}
                    onChange={handleToggleChange}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span
                  className={`toggle-label ${
                    formData.studentType === "test" ? "active" : ""
                  }`}
                >
                  Test Student
                </span>
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-cancel"
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-register">
              Allot Chair
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChairRegistrationModal;
